/** Puntero láser: rastro que se desvanece y se transmite a los colaboradores. */
import type { AppContext, PointerInfo, Tool } from './types.ts'

const TRAIL_MS = 900

export class LaserTool implements Tool {
  readonly id = 'laser' as const
  readonly cursor = 'crosshair'
  private ctx: AppContext
  private trail: { x: number; y: number; t: number }[] = []
  private active = false
  private removePainter: (() => void) | null = null
  private raf = 0

  constructor(ctx: AppContext) {
    this.ctx = ctx
  }

  onActivate(): void {
    this.removePainter = this.ctx.renderer.addPainter((c) => this.paint(c))
    this.tick()
  }

  onDeactivate(): void {
    this.removePainter?.()
    this.removePainter = null
    cancelAnimationFrame(this.raf)
    this.trail = []
    this.ctx.setLaser(null)
  }

  onPointerDown(p: PointerInfo): void {
    this.active = true
    this.trail = [{ x: p.x, y: p.y, t: performance.now() }]
  }

  onPointerMove(p: PointerInfo): void {
    if (!this.active) return
    this.trail.push({ x: p.x, y: p.y, t: performance.now() })
    if (this.trail.length > 220) this.trail.shift()
  }

  onPointerUp(): void {
    this.active = false
  }

  onCancel(): void {
    this.active = false
  }

  /** Mantiene el rastro vivo aunque el puntero esté quieto (se va desvaneciendo). */
  private tick = (): void => {
    this.raf = requestAnimationFrame(this.tick)
    const now = performance.now()
    const before = this.trail.length
    this.trail = this.trail.filter((p) => now - p.t < TRAIL_MS)
    if (this.trail.length !== before || this.active) {
      this.ctx.renderer.invalidateOverlay()
      this.publish()
    }
  }

  private publish(): void {
    if (this.trail.length < 2) {
      this.ctx.setLaser(null)
      return
    }
    const flat: number[] = []
    for (const p of this.trail) flat.push(Math.round(p.x * 10) / 10, Math.round(p.y * 10) / 10)
    this.ctx.setLaser(flat)
  }

  private paint(c: CanvasRenderingContext2D): void {
    if (this.trail.length < 2) return
    const inv = 1 / this.ctx.store.camera.z
    const now = performance.now()
    c.save()
    c.lineCap = 'round'
    c.lineJoin = 'round'
    c.strokeStyle = '#FF2D2D'
    c.shadowColor = 'rgba(255,45,45,0.8)'
    c.shadowBlur = 14 * inv
    for (let i = 1; i < this.trail.length; i++) {
      const age = (now - this.trail[i].t) / TRAIL_MS
      c.globalAlpha = Math.max(0, 1 - age) * 0.9
      c.lineWidth = (2.5 + (1 - age) * 3.5) * inv
      c.beginPath()
      c.moveTo(this.trail[i - 1].x, this.trail[i - 1].y)
      c.lineTo(this.trail[i].x, this.trail[i].y)
      c.stroke()
    }
    c.restore()
  }
}

/** Herramienta mano: desplaza el lienzo. */
export class HandTool implements Tool {
  readonly id = 'hand' as const
  readonly cursor = 'grab'
  private ctx: AppContext
  private start: { x: number; y: number; camX: number; camY: number } | null = null

  constructor(ctx: AppContext) {
    this.ctx = ctx
  }

  onPointerDown(p: PointerInfo): void {
    const { camera } = this.ctx.store
    this.start = { x: p.sx, y: p.sy, camX: camera.x, camY: camera.y }
  }

  onPointerMove(p: PointerInfo): void {
    if (!this.start) return
    const { camera } = this.ctx.store
    this.ctx.store.setCamera({
      x: this.start.camX - (p.sx - this.start.x) / camera.z,
      y: this.start.camY - (p.sy - this.start.y) / camera.z,
    })
  }

  onPointerUp(): void {
    this.start = null
  }
}
